import { ChevronDown } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../../components/ui/dropdown-menu'
import { WriteButton } from '../IngestPage/shared/WriteButton'

export type EditAction = 'instruction' | 'checks' | 'description' | 'contract' | 'archive'

/**
 * The latest version's *Edit ▾*. *Edit with agent* comes first — the usual
 * way to change a config: a note for the builder, with new example files if
 * the change needs them. Under it are the edits a person makes by hand, then
 * Archive. Items that start a build are disabled while one is in flight; on
 * an agent config that includes *Edit description*, because the description
 * is the extractor's prompt and the example pages are read again with it.
 */
export function EditMenu({
  parserKind,
  canManage,
  buildInFlight,
  onAction
}: {
  parserKind: 'script' | 'agent'
  canManage: boolean
  buildInFlight: boolean
  onAction: (action: EditAction) => void
}) {
  const rebuildTitle = buildInFlight ? 'A build is already in flight for this config' : undefined
  const agent = parserKind === 'agent'
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <WriteButton
          canManage={canManage}
          size="sm"
          variant="outline"
          data-testid="config-edit-menu"
        >
          Edit
          <ChevronDown className="size-3.5" aria-hidden />
        </WriteButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          disabled={buildInFlight}
          title={rebuildTitle}
          onSelect={() => onAction('instruction')}
        >
          Edit with agent…
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => onAction('checks')}>Edit checks…</DropdownMenuItem>
        <DropdownMenuItem
          disabled={agent && buildInFlight}
          title={agent ? rebuildTitle : undefined}
          onSelect={() => onAction('description')}
        >
          {agent ? 'Edit description… (re-reads pages)' : 'Edit description…'}
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={buildInFlight}
          title={rebuildTitle}
          onSelect={() => onAction('contract')}
        >
          Edit contract… (rebuilds)
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onSelect={() => onAction('archive')}>
          Archive config…
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
